import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../utils/baseQuery';
import {
  AddressPrediction,
  AddressValidationRequest,
  AddressValidationResult,
  GeocodeType,
  ParsedPlace,
  PlaceAddressComponent,
  PlaceAutocompleteResponse,
  PlaceDetailsResponse,
} from './google.interface';

declare const window: {
  _env_: {
    REACT_APP_GOOGLE_MAP_API_KEY: string;
  };
};

// parseNewPlaceComponents maps Places API (New) addressComponents to our form
// fields. Street = street_number + route; city falls back through the common
// alternatives Google returns; state uses the 2-letter shortText.
export const parseNewPlaceComponents = (components: PlaceAddressComponent[] = []): Pick<ParsedPlace, 'address_1' | 'city' | 'state' | 'postal'> => {
  const byType = (type: string): PlaceAddressComponent | undefined => components.find((c) => c.types?.includes(type));
  const streetNumber = byType('street_number')?.longText ?? '';
  const route = byType('route')?.longText ?? '';
  const city = byType('locality')?.longText ?? byType('postal_town')?.longText ?? byType('sublocality')?.longText ?? '';
  const state = byType('administrative_area_level_1')?.shortText ?? '';
  const postal = byType('postal_code')?.longText ?? '';
  return { address_1: `${streetNumber} ${route}`.trim(), city, state, postal };
};
export const googleApi = createApi({
  reducerPath: 'policiesApi',
  baseQuery: baseQuery(''),
  tagTypes: ['Geocode'],
  endpoints: (build) => ({
    getGeocode: build.query<GeocodeType, { latlng?: string; address?: string }>({
      query: (params) => ({
        url: 'https://maps.googleapis.com/maps/api/geocode/json',
        method: 'GET',
        ...(params && { params: { ...params, key: window._env_.REACT_APP_GOOGLE_MAP_API_KEY } }),
      }),
      transformResponse: (response: { results: GeocodeType }) => response?.results,
      providesTags: (result, error, arg) => (result ? [{ type: 'Geocode', id: arg?.latlng }] : ['Geocode']),
    }),
    // Google Address Validation API — standardizes/validates a US address and
    // returns a CONFIRM/FIX/ACCEPT verdict plus the corrected address. Failures
    // are non-fatal (hideToastError), so callers can fall back to saving as-is.
    validateAddress: build.mutation<AddressValidationResult, AddressValidationRequest>({
      query: ({ addressLines, locality, administrativeArea, postalCode }) => ({
        url: 'https://addressvalidation.googleapis.com/v1:validateAddress',
        method: 'POST',
        params: { key: window._env_.REACT_APP_GOOGLE_MAP_API_KEY },
        body: { address: { regionCode: 'US', addressLines, locality, administrativeArea, postalCode } },
        hideToastError: true,
      }),
    }),
    // Places API (New) autocomplete — returns address predictions for the typed
    // input. sessionToken groups the keystrokes + the final placeDetails call into
    // one billable session. Failures are non-fatal (hideToastError) so the field
    // silently falls back to manual entry when the API is disabled.
    placeAutocomplete: build.mutation<AddressPrediction[], { input: string; sessionToken: string }>({
      query: ({ input, sessionToken }) => ({
        url: 'https://places.googleapis.com/v1/places:autocomplete',
        method: 'POST',
        params: { key: window._env_.REACT_APP_GOOGLE_MAP_API_KEY },
        body: { input, sessionToken, includedRegionCodes: ['us'] },
        hideToastError: true,
      }),
      transformResponse: (response: PlaceAutocompleteResponse): AddressPrediction[] =>
        (response.suggestions ?? [])
          .map((s) => s.placePrediction)
          .filter((p): p is NonNullable<typeof p> => !!p?.placeId)
          .map((p) => ({
            placeId: p.placeId,
            primaryText: p.structuredFormat?.mainText?.text ?? p.text?.text ?? '',
            secondaryText: p.structuredFormat?.secondaryText?.text ?? '',
            description: p.text?.text ?? '',
          })),
    }),
    // Places API (New) details for a selected prediction — the field mask (via the
    // `fields` param) limits billing to the address components + location we need.
    getPlaceDetails: build.query<ParsedPlace, { placeId: string; sessionToken: string }>({
      query: ({ placeId, sessionToken }) => ({
        url: `https://places.googleapis.com/v1/places/${placeId}`,
        method: 'GET',
        params: { key: window._env_.REACT_APP_GOOGLE_MAP_API_KEY, sessionToken, fields: 'addressComponents,location,formattedAddress' },
        hideToastError: true,
      }),
      transformResponse: (response: PlaceDetailsResponse): ParsedPlace => ({
        ...parseNewPlaceComponents(response.addressComponents),
        lat: response.location?.latitude ?? 0,
        lng: response.location?.longitude ?? 0,
        formattedAddress: response.formattedAddress ?? '',
      }),
    }),
  }),
});

export const { useLazyGetGeocodeQuery, useValidateAddressMutation, usePlaceAutocompleteMutation, useLazyGetPlaceDetailsQuery } = googleApi;
